import { GatsbyImage } from 'gatsby-plugin-image';
import React from 'react';
import styled from 'styled-components';
import { getIcon } from '../../../utils';
import {
  AnchorText,
  Col,
  ContactInfoList,
  Container,
  Form,
  Grid,
  Overlay,
  SanityBlockContent,
  Section,
} from '../../common';

const StyledHeadline = styled.h1`
  color: ${(props) => props.$headline} !important;
`;

const StyledSubheadline = styled.div`
  a {
    color: ${(props) => props.$aColor} !important;
    &:hover {
      color: ${(props) => props.$aColorHover} !important;
    }
  }
`;

const StyledIcon = styled.span`
  svg {
    color: ${(props) => props.$color};
    &:hover {
      color: ${(props) => props.$colorHover};
    }
  }
`;

const HeroContact = ({ sanity, page }) => {
  const { colors, business } = sanity;
  const { gbp, _rawHeroSubheadline, heroBgImg, heroHeadline, heroTemplate } =
    page;

  const physicalAddress = `${gbp?.address}, ${gbp?.city}, ${gbp?.stateShort} ${gbp?.zip}`;

  const socials = [
    { id: 1, icon: 'Facebook', url: business?.facebookUrl },
    { id: 2, icon: 'Instagram', url: business?.instagramUrl },
    { id: 3, icon: 'Twitter', url: business?.twitterUrl },
    { id: 4, icon: 'LinkedIn', url: business?.linkedInUrl },
    { id: 5, icon: 'YouTube', url: business?.youTubeUrl },
    { id: 6, icon: 'TikTok', url: business?.tikTokUrl },
    { id: 7, icon: 'Yelp', url: business?.yelpUrl },
  ];

  return (
    <Section
      padding="custom"
      idName="hero-contact"
      classes="relative flex flex-col place-content-center place-items-center pt-[10rem] lg:pt-[15rem] pb-[8rem] lg:pb-[6rem]"
    >
      <div className="absolute inset-0">
        <GatsbyImage
          image={heroBgImg?.asset?.gatsbyImageData}
          alt={heroBgImg?.alt}
          className="object-cover w-full h-full"
        />
        <Overlay
          isGradient
          bgColor={colors?.neutral?.default}
          bgColorHover={colors?.neutral?.dark}
          bgColorStart={colors?.neutral?.darker}
          bgColorMid={colors?.neutral?.dark}
          bgColorEnd={colors?.neutral?.default}
        />
      </div>
      <Grid
        classes={`lg:grid-cols-5 gap-y-0 max-w-7xl place-items-center space-y-24 ${
          heroTemplate === 'textContactForm' ? 'lg:gap-24' : 'lg:gap-0'
        }`}
      >
        {/* Left Column: Contact Details */}
        <Col classes="lg:col-span-3 px-4 lg:px-0 relative">
          <div className="relative px-6 py-10 overflow-hidden bg-zinc-700 sm:px-10 xl:p-12">
            {/* Responsive SVG backgrounds */}
            <div
              className="absolute inset-0 pointer-events-none sm:hidden"
              aria-hidden="true"
            >
              <svg
                className="absolute inset-0 w-full h-full"
                width={343}
                height={388}
                viewBox="0 0 343 388"
                fill="none"
                preserveAspectRatio="xMidYMid slice"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M-99 461.107L608.107-246l707.103 707.107-707.103 707.103L-99 461.107z"
                  fill="url(#linear1)"
                  fillOpacity=".1"
                />
                <defs>
                  <linearGradient
                    id="linear1"
                    x1="254.553"
                    y1="107.554"
                    x2="961.66"
                    y2="814.66"
                  >
                    <stop stopColor="#fff" />
                    <stop offset={1} stopColor="#fff" stopOpacity={0} />
                  </linearGradient>
                </defs>
              </svg>
            </div>
            <div
              className="absolute top-0 bottom-0 right-0 hidden w-1/2 pointer-events-none sm:block lg:hidden"
              aria-hidden="true"
            >
              <svg
                className="absolute inset-0 w-full h-full"
                width={359}
                height={339}
                viewBox="0 0 359 339"
                fill="none"
                preserveAspectRatio="xMidYMid slice"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M-161 382.107L546.107-325l707.103 707.107-707.103 707.103L-161 382.107z"
                  fill="url(#linear2)"
                  fillOpacity=".1"
                />
                <defs>
                  <linearGradient
                    id="linear2"
                    x1="192.553"
                    y1="28.553"
                    x2="899.66"
                    y2="735.66"
                  >
                    <stop stopColor="#fff" />
                    <stop offset={1} stopColor="#fff" stopOpacity={0} />
                  </linearGradient>
                </defs>
              </svg>
            </div>
            <div
              className="absolute top-0 bottom-0 right-0 hidden w-1/2 pointer-events-none lg:block"
              aria-hidden="true"
            >
              <svg
                className="absolute inset-0 w-full h-full"
                width={160}
                height={678}
                viewBox="0 0 160 678"
                fill="none"
                preserveAspectRatio="xMidYMid slice"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M-161 679.107L546.107-28l707.103 707.107-707.103 707.103L-161 679.107z"
                  fill="url(#linear3)"
                  fillOpacity=".1"
                />
                <defs>
                  <linearGradient
                    id="linear3"
                    x1="192.553"
                    y1="325.553"
                    x2="899.66"
                    y2="1032.66"
                  >
                    <stop stopColor="#fff" />
                    <stop offset={1} stopColor="#fff" stopOpacity={0} />
                  </linearGradient>
                </defs>
              </svg>
            </div>
            {/* Replace FadeIn with individual Containers */}
            <Container
              disableAnimations={false}
              padding="none"
              animationDelay={0}
            >
              <StyledHeadline
                $headline={colors?.primary?.lighter}
                className="!lg:text-4xl !text-3xl !font-bold"
              >
                {heroHeadline}
              </StyledHeadline>
            </Container>
            <Container
              disableAnimations={false}
              padding="none"
              animationDelay={0.2}
            >
              <StyledSubheadline
                $aColor={colors?.accent?.light}
                $aColorHover={colors?.accent?.lighter}
                className="max-w-3xl mt-6 mb-10 text-base text-white"
              >
                <SanityBlockContent
                  sanity={sanity}
                  blocks={_rawHeroSubheadline}
                />
              </StyledSubheadline>
            </Container>
            <Container
              disableAnimations={false}
              padding="none"
              animationDelay={0.4}
            >
              <ContactInfoList page={page} sanity={sanity} />
            </Container>
            <Container
              disableAnimations={false}
              padding="none"
              animationDelay={0.6}
            >
              <ul className="flex mt-12 space-x-6">
                {socials?.map((social) => {
                  if (social?.url) {
                    const SocialIconComponent = getIcon(social?.icon);
                    return (
                      <li key={social?.icon}>
                        <AnchorText
                          sanity={sanity}
                          type="external"
                          color={colors?.neutral?.light}
                          colorHover={colors?.accent?.light}
                          href={social?.url}
                        >
                          <span className="sr-only">{social?.icon}</span>
                          <StyledIcon
                            $color={colors?.accent?.default}
                            $colorHover={colors?.accent?.lighter}
                            className="!text-3xl"
                          >
                            <SocialIconComponent />
                          </StyledIcon>
                        </AnchorText>
                      </li>
                    );
                  }
                  return null;
                })}
              </ul>
            </Container>
          </div>
        </Col>
        {/* Right Column: Form or Iframe */}
        {heroTemplate === 'textContactForm' ? (
          <Col classes="lg:col-span-2 w-full h-full px-3 lg:px-0">
            <Form page={page} sanity={sanity} formName="form-hero" />
          </Col>
        ) : (
          <Col classes="lg:col-span-2 w-full h-96 lg:h-full z-10 px-4 lg:px-0">
            <iframe
              src={gbp?.gbpEmbed}
              width="100%"
              height="100%"
              style={{ border: 0 }}
              allowFullScreen=""
              loading="lazy"
              title="Contact Map Embed"
            />
          </Col>
        )}
      </Grid>
    </Section>
  );
};

export { HeroContact };
